import { ReactNode, useCallback } from 'react';
import styled from '@emotion/styled';
import { Formik, FormikProps, FormikConfig, FormikHelpers } from 'formik';
import { Form, Tooltip } from 'antd';
import { useTranslate } from '@tolgee/react';
import { mustache2SlateValuePolicyMsgTypes } from 'components/TemplateEditor/mustacheUtils';
import { What } from 'views/Marketing/components/PolicyTable';
import { PolicyFormValues, useMsgTypes } from './share';
import NameField, { validate as nameValidate } from './NameField';
import EnableField, { validate as enableValidate } from './EnableField';
import TermOfValidityField from './TermOfValidityField';
// import BilingualExecuteContextField,
// import { validate as bilingualExecuteContextValidate } from './BilingualExecuteContextField';
import BilingualExecuteContextsFieldV2, {
  validate as bilingualExecuteContextsValidate,
} from './BilingualExecuteContextsFieldV2';
import ShippingStatusField, { validate as shippingStatusValidate } from './ShippingStatusField';
import AutoCancelField, { validate as autoCancelValidate } from './AutoCancelField';
import CancelTradeContentsField, {
  validate as cancelTradeContentsValidate,
} from './CancelTradeContentsField';
import StarsField, { validate as starsValidate } from './StarsField';
import AutoReviewContentsField, {
  validate as autoReviewContentsValidate,
} from './AutoReviewContents';
import AutoReviewDelayDay from './AutoReviewDelayDay';
import {
  cancellingTradeScenarios,
  isNoRegionScenario,
  isPositiveReviewScenarioType,
  isReviewManagementScenarioType,
  lazadaUnLimitMessageCountScenarios,
  negativeReviewStars,
  positiveReviewStars,
  tiktokCancelTradeScenarios,
} from '../config';
import ItemLimitField from './ItemLimitField';
import ReturnTypeField from './ReturnTypeField';
import DelayShipDelayDayField from './DelayShipDelayDayField';
import PayMethodField from './PayMethodField';
import OrderAmountField from './OrderAmountField';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';

type FV = PolicyFormValues;

export type PolicyFormHelpers = FormikHelpers<FV>;

type FormChildren<FV> = (form: ReactNode, formProps: FormikProps<FV>) => ReactNode;

export interface PolicyFormProps extends Omit<FormikConfig<FV>, 'initialValues'> {
  initialValues?: FV;
  children: FormChildren<FV>;
  scenarioType: Marketing.AllScenarioType;
  platform: General.PlatformUpperCase;
  region: General.UpperCaseRegion;
  existNameList: string[];
  taskId: number;
  round?: number;
}

export const createDefaultInitialValues = (
  scenarioType: Marketing.AllScenarioType,
  platform: General.PlatformUpperCase,
): FV => {
  return {
    name: '',
    enable: true,
    termOfValidity: {
      type: 'LONG_TERM',
      dateTimeQuantum: null,
      timeQuantumGroup: null,
    },
    filterItem: {
      type: 'NO_FILTER',
      specificSPUs: [],
      specificSKUs: [],
      specificCategories: [],
    },
    bilingualExecuteContext: {
      content: mustache2SlateValuePolicyMsgTypes(''),
      translatedContent: mustache2SlateValuePolicyMsgTypes(''),
    },
    bilingualExecuteContexts: [
      {
        content: mustache2SlateValuePolicyMsgTypes(''),
        translatedContent: mustache2SlateValuePolicyMsgTypes(''),
      },
    ],
    executeItemContext: [],
    cancelTradeConfig: {
      shippingStatus: ['TO_PROCESS'],
      autoCancel: false,
      autoCancelLimitHour: 0,
      contents: [
        {
          sendPoint:
            (isTiKTokUpperCase(platform) &&
              tiktokCancelTradeScenarios.includes(scenarioType as any)) ||
            scenarioType === 'CANCELLING_DELIVERY_FAIL'
              ? 'CANCELLED'
              : 'IN_CANCEL',
          executeContext: {
            content: mustache2SlateValuePolicyMsgTypes(''),
            translatedContent: mustache2SlateValuePolicyMsgTypes(''),
          },
        },
      ],
    },
    autoReviewConfig: {
      stars: isPositiveReviewScenarioType(scenarioType) ? positiveReviewStars : negativeReviewStars,
      contents: platform === 'LAZADA' ? ['ANY'] : ['ANY', 'EMPTY'],
      delayAutoReview:
        ['SHOPEE', 'LAZADA'].includes(platform) && isPositiveReviewScenarioType(scenarioType),
      delayDay:
        ['SHOPEE', 'LAZADA'].includes(platform) && isPositiveReviewScenarioType(scenarioType)
          ? 5
          : 0,
    },
    limitItem: {
      type: 'ALL',
      specificItems: [],
    },
    delayShipPolicyConfig: {
      delayDay: 3,
    },
    limitRule: {
      limitPayMethod: {
        enable: false,
        payMethods: [],
      },
      limitOrderAmount: {
        enable: false,
        operate: 'GT',
        args: [0],
      },
      limitRefundOrderAmount: {
        enable: false,
        operate: 'GT',
        args: [0],
      },
      limitOrderStatus: {
        enable: false,
        orderStatus: [],
      },
    },
    returnTradeConfig: {
      returnType: undefined!,
      autoRefund: false,
      refundRatio: 0,
    },
  };
};

export const StyledForm = styled(Form)`
  > div {
    flex: 1 0 0;
    margin-top: 16px;
    padding: 24px;
    background: #fff;
    border-radius: 2px;
    > h3 {
      position: relative;
      font-size: 16px;
      text-indent: 12px;
      &::before {
        display: block;
        content: '';
        position: absolute;
        top: 6px;
        left: 0;
        width: 4px;
        height: 14px;
        background: #3370ff;
      }
      > span {
        margin-left: 8px;
        font-size: 14px;
        font-weight: 400;
        color: #515359;
      }
    }
    > .ant-form-item {
      .ant-form-item-label {
        flex: 0 0 auto;
        min-width: 120px;
      }
      .ant-form-item-control {
        flex: 1 0 0;
      }
    }
  }
`;

function PolicyForm(props: PolicyFormProps) {
  const {
    children,
    scenarioType,
    platform,
    region,
    existNameList,
    initialValues = createDefaultInitialValues(scenarioType, platform),
    taskId,
    round,
    ...otherProps
  } = props;

  const { t } = useTranslate();

  const isCancellingTrade = cancellingTradeScenarios.includes(
    scenarioType as Marketing.CancellingTradeScenarioType,
  );

  const isReviewManagement = isReviewManagementScenarioType(scenarioType);

  const isNoRegion = isNoRegionScenario(scenarioType);

  const validate = useCallback(
    (values: FV) => {
      const errors: Partial<Record<keyof typeof values, any>> = {};

      if (isCancellingTrade || scenarioType === 'CANCELLING_DELIVERY_FAIL') {
        nameValidate(errors, values, t, existNameList);
        enableValidate(errors, values, t);
        if (platform === 'SHOPEE' && scenarioType !== 'CANCELLING_DELIVERY_FAIL') {
          shippingStatusValidate(errors, values, t);
          autoCancelValidate(errors, values, t);
        }
        cancelTradeContentsValidate(errors, values, t, platform);
        return errors;
      }

      if (isReviewManagement) {
        nameValidate(errors, values, t, existNameList);
        enableValidate(errors, values, t);
        starsValidate(errors, values, t);
        autoReviewContentsValidate(errors, values, t);
        bilingualExecuteContextsValidate(errors, values, t, platform, scenarioType);
        return errors;
      }

      if (isNoRegion) {
        if (scenarioType === 'RECEPTION_SCORE') {
          return errors;
        }

        nameValidate(errors, values, t, existNameList);
        enableValidate(errors, values, t);

        return errors;
      }

      nameValidate(errors, values, t, existNameList);
      enableValidate(errors, values, t);
      // bilingualExecuteContextValidate(errors, values, t, platform);
      bilingualExecuteContextsValidate(errors, values, t, platform, scenarioType);
      return errors;
    },
    [t, scenarioType, isCancellingTrade, isNoRegion, isReviewManagement, platform, existNameList],
  );

  const msgTypes = useMsgTypes(platform, scenarioType);

  return (
    <Formik
      enableReinitialize
      validateOnMount
      initialValues={initialValues}
      validate={validate}
      {...otherProps}
    >
      {(formProps) => {
        if (isNoRegion) {
          return children(null, formProps);
        }

        const sendContentTips = platform === 'LAZADA' &&
          !lazadaUnLimitMessageCountScenarios.includes(scenarioType) && (
            <Tooltip
              title={t(
                'aide.因Lazada平台限制，买家不回复消息最多允许店铺发送5条/天消息。策略中的发送内容最多可添加5条。',
              )}
            >
              <What style={{ textIndent: 0 }} />
            </Tooltip>
          );

        if (isCancellingTrade || scenarioType === 'CANCELLING_DELIVERY_FAIL') {
          const form = (
            <StyledForm colon>
              <div>
                <h3>{t('aide.基础配置')}</h3>
                <NameField />
                <EnableField />
                <TermOfValidityField scenarioType={scenarioType} region={region} />
                <ItemLimitField taskId={taskId} platform={platform} region={region} />
                <ReturnTypeField scenarioType={scenarioType} />
                <PayMethodField scenarioType={scenarioType} platform={platform} region={region} />
                <OrderAmountField scenarioType={scenarioType} region={region} />
              </div>
              {platform === 'SHOPEE' && scenarioType !== 'CANCELLING_DELIVERY_FAIL' && (
                <div>
                  <h3>{t('aide.订单规则')}</h3>
                  <ShippingStatusField />
                  <AutoCancelField />
                </div>
              )}
              <div>
                <h3>
                  {t('reviewSetting.发送内容')}
                  {sendContentTips}
                </h3>
                <CancelTradeContentsField
                  scenarioType={scenarioType}
                  platform={platform}
                  region={region}
                  msgTypes={msgTypes}
                />
              </div>
            </StyledForm>
          );

          return children(form, formProps);
        }

        if (isReviewManagementScenarioType(scenarioType)) {
          const form = (
            <StyledForm colon>
              <div>
                <h3>{t('aide.基础配置')}</h3>
                <NameField />
                <EnableField />
                <ItemLimitField
                  taskId={taskId}
                  platform={platform}
                  region={region}
                  skuEnable={false}
                />
                <PayMethodField scenarioType={scenarioType} platform={platform} region={region} />
                <OrderAmountField scenarioType={scenarioType} region={region} />
              </div>
              <div>
                <h3>{t('aide.评价规则')}</h3>
                <StarsField
                  scenarioType={scenarioType}
                  taskId={taskId}
                  platform={platform}
                  region={region}
                />
                <AutoReviewContentsField platform={platform} />
                <AutoReviewDelayDay scenarioType={scenarioType} />
              </div>
              <div>
                <h3>
                  {t('reviewSetting.发送内容')}
                  {sendContentTips}
                </h3>
                <BilingualExecuteContextsFieldV2
                  scenarioType={scenarioType}
                  platform={platform}
                  region={region}
                  msgTypes={msgTypes}
                  round={round}
                />
              </div>
            </StyledForm>
          );

          return children(form, formProps);
        }

        const form = (
          <StyledForm colon>
            <div>
              <h3>{t('aide.基础配置')}</h3>
              <NameField />
              <EnableField />
              <TermOfValidityField scenarioType={scenarioType} region={region} />
              {scenarioType !== 'ORDER_REMINDER' && (
                <ItemLimitField taskId={taskId} platform={platform} region={region} />
              )}
              <ReturnTypeField scenarioType={scenarioType} />
              {scenarioType === 'DELAY_SHIP' && <DelayShipDelayDayField />}
              <PayMethodField scenarioType={scenarioType} platform={platform} region={region} />
              <OrderAmountField scenarioType={scenarioType} region={region} />
            </div>
            <div>
              <h3>
                {t('reviewSetting.发送内容')}
                {sendContentTips}
              </h3>
              <BilingualExecuteContextsFieldV2
                scenarioType={scenarioType}
                platform={platform}
                region={region}
                msgTypes={msgTypes}
                round={round}
              />
            </div>
          </StyledForm>
        );

        return children(form, formProps);
      }}
    </Formik>
  );
}

export default PolicyForm;
export * from './share';
